import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaCar,
  FaMapMarkerAlt,
  FaCalendar,
  FaSearch,
  FaFilter,
  FaEye,
  FaEdit,
  FaDownload,
  FaUser,
  FaPhone,
  FaClock,
  FaDollarSign,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaColumns,
  FaTimes,
  FaHistory,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import RideDetailModal from "../components/RideDetailModal";
import RideStatusUpdateModal from "../components/RideStatusUpdateModal";
import MobileNumberSharedModal from "../components/MobileNumberSharedModal";
import ActivityLogsModal from "../components/ActivityLogsModal";
import RidesStatusStatsPanel from "../components/RidesStatusStatsPanel";
import RideReportDateFilters from "../components/RideReportDateFilters";
import SortableTh from "../components/SortableTh";
import RidesAssignedEmployeeStatsPanel from "../components/RidesAssignedEmployeeStatsPanel";
import { formatDuration } from "../utils/formatDuration";
import { getEstimatedRouteTimes, getFinalRouteTimes } from "../utils/rideRouteTimes";
import { useProfile } from "../context/ProfileContext";
import { STATE_UT_OPTIONS, getStateUtDisplayLabel } from "../constants/stateUtOptions";

const getRideCustomerRating = (ride) => {
  const fromUser = ride?.userRating?.value;
  if (fromUser !== null && fromUser !== undefined && fromUser !== "") {
    const n = typeof fromUser === "number" ? fromUser : Number(fromUser);
    if (Number.isFinite(n)) return n;
  }
  const fromDoc = ride?.customerRideRating?.rating;
  if (fromDoc !== null && fromDoc !== undefined && fromDoc !== "") {
    const n = typeof fromDoc === "number" ? fromDoc : Number(fromDoc);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getRideCustomerReview = (ride) => {
  const fb = ride?.userRating?.feedback;
  if (typeof fb === "string" && fb.trim()) return fb.trim();
  const rv = ride?.customerRideRating?.review;
  if (typeof rv === "string" && rv.trim()) return rv.trim();
  return "";
};

const getRideDriverRating = (ride) => {
  const fromDriver = ride?.driverRating?.value;
  if (fromDriver !== null && fromDriver !== undefined && fromDriver !== "") {
    const n = typeof fromDriver === "number" ? fromDriver : Number(fromDriver);
    if (Number.isFinite(n)) return n;
  }
  const fromDoc = ride?.driverRideRating?.rating;
  if (fromDoc !== null && fromDoc !== undefined && fromDoc !== "") {
    const n = typeof fromDoc === "number" ? fromDoc : Number(fromDoc);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getRideDriverReview = (ride) => {
  const fb = ride?.driverRating?.feedback;
  if (typeof fb === "string" && fb.trim()) return fb.trim();
  const rv = ride?.driverRideRating?.review;
  if (typeof rv === "string" && rv.trim()) return rv.trim();
  return "";
};

const formatRideRatingCell = (value) => {
  if (value == null || !Number.isFinite(value)) return "N/A";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}/5`;
};

const formatRideMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const formatCommissionColumn = (ride) => {
  const c = ride.commission;
  if (c == null || c === "") return "—";
  const n = Number(c);
  if (!Number.isFinite(n)) return "—";
  return formatRideMoney(n);
};

const formatTaxColumn = (ride) => {
  const g = ride.gstAmount;
  if (g == null || g === "") return "—";
  const n = Number(g);
  if (!Number.isFinite(n)) return "—";
  return formatRideMoney(n);
};

function DueStatusCell({ ride }) {
  const status = ride.dueStatus;
  if (!status) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  const cls =
    status === "Paid"
      ? "text-green-600"
      : status === "Failed"
        ? "text-red-600"
        : "text-yellow-600";
  return <span className={`text-sm font-medium ${cls}`}>{status}</span>;
}

function ExpandableReviewText({ text }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!text) {
    return null;
  }

  const popup =
    open &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
        role="presentation"
        onClick={() => setOpen(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="rides-review-popup-title"
          className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[min(70vh,32rem)] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
            <h3
              id="rides-review-popup-title"
              className="text-sm font-semibold text-gray-900"
            >
              Review
            </h3>
            <button
              type="button"
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              onClick={() => setOpen(false)}
              aria-label="Close review"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-3 overflow-y-auto text-sm text-gray-800 whitespace-pre-wrap">
            {text}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        type="button"
        className="text-xs p-0 bg-transparent border-0 cursor-pointer text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="font-medium text-gray-600">Review:</span>{" "}
        <span className="text-blue-600 hover:text-blue-800 font-medium underline-offset-2 hover:underline">
          View review
        </span>
      </button>
      {popup}
    </>
  );
} 

const RIDE_TABLE_COLUMN_OPTIONS = [
  ["rideId", "Ride ID"],
  ["rideCreated", "Ride Created"],
  ["rideScheduled", "Ride Scheduled"],
  ["estimatedRoute", "Estimated Route"],
  ["finalRoute", "Final Route"],
  ["started", "Started"],
  ["completed", "Completed"],
  ["rideDetails", "Ride Details"],
  ["commission", "Commission (10%)"],
  ["tax", "Tax"],
  ["dueStatus", "Due Status"],
  ["customer", "Customer"],
  ["driver", "Driver"],
  ["assignedEmployee", "Assigned Employee"],
  ["assignedBy", "Assigned By"],
  ["rideStatus", "Ride Status"],
  ["reason", "Reason"],
  ["mobileNumberShared", "Mobile Number Shared"],
  ["mobileNumberSharedParty", "Shared With"],
  ["mobileNumberSharedCallerId", "Caller ID"],
  ["mobileNumberSharedTimes", "Shared Times"],
];

const Rides = () => {
  const { profile, assignModule } = useProfile();
  const isNewType = profile?.employeeType === "new";
  const ridesModule = isNewType && assignModule?.rides?.enabled ? assignModule.rides : null;
  const canViewRides = !isNewType || (ridesModule && ridesModule.actions?.view !== false);
  const canEditRides = !isNewType || (ridesModule && !!ridesModule.actions?.edit);
  const isSuperAdmin =
    profile?.role?.slug === "superadmin" ||
    profile?.role?.name === "SuperAdmin" ||
    profile?.email === "superadmin@udrivo.in";
  const canAssignRides = isSuperAdmin || (ridesModule && ridesModule.fullAccess);
  const canDeleteRides = !isNewType || (ridesModule && !!ridesModule.actions?.delete);

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  // Filters and Search
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: "",
    rideCategory: "",
    tripType: "",
    paymentStatus: "",
    paymentMethod: "",
    scheduleType: "",
    userId: "",
    driverId: "",
    startDate: "",
    endDate: "",
    city: "",
    stateUt: "",
    rideId: "",
    estimatedPickupLocation: "",
    customerSearch: "",
    driverSearch: "",
    assignedEmployeeId: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    fare: "",
    search: "",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRides: 0,
    hasNext: false,
    hasPrev: false,
    limit: 20,
  });

  // Modals
  const [detailModal, setDetailModal] = useState({ show: false, ride: null });
  const [statusModal, setStatusModal] = useState({ show: false, ride: null });
  const [mobileShareModal, setMobileShareModal] = useState({
    show: false,
    ride: null,
    revertToNo: false,
  });
  const [activityLogsModal, setActivityLogsModal] = useState({
    show: false,
    module: "Ride",
    entityId: null,
  });
  const [selectedRideIds, setSelectedRideIds] = useState([]);
  const [showAssignRidesModal, setShowAssignRidesModal] = useState(false);
  const [assignRidesEmployeeId, setAssignRidesEmployeeId] = useState("");
  const [assignRidesAssignedDate, setAssignRidesAssignedDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [newEmployeesList, setNewEmployeesList] = useState([]);
  const [assignedEmployeeFilterOptions, setAssignedEmployeeFilterOptions] = useState([]);
  const [assignRidesLoading, setAssignRidesLoading] = useState(false);
  const [assignRidesSubmitLoading, setAssignRidesSubmitLoading] = useState(false);
  const tableScrollContainerRef = useRef(null);
  const stateUtDropdownRef = useRef(null);
  const [stateUtDropdownOpen, setStateUtDropdownOpen] = useState(false);
  const [stateUtSearch, setStateUtSearch] = useState("");
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    rideId: true,
    rideCreated: true,
    rideScheduled: true,
    rideDetails: true,
    commission: true,
    tax: true,
    dueStatus: true,
    customer: true,
    driver: true,
    assignedEmployee: true,
    assignedBy: true,
    estimatedRoute: true,
    finalRoute: true,
    rideStatus: true,
    reason: true,
    started: true,
    completed: true,
    mobileNumberShared: true,
    mobileNumberSharedParty: false,
    mobileNumberSharedCallerId: true,
    mobileNumberSharedTimes: true,
  });

  // Fetch rides with filters
  const fetchRides = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: filters.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (
          value &&
          typeof value === "string" &&
          value.trim() &&
          key !== "page" &&
          key !== "limit" &&
          key !== "sortBy" &&
          key !== "sortOrder"
        ) {
          queryParams.append(key, value);
        }
      });

      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_LIST}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setRides(response.data.data.rides || []);
        setPagination({
          currentPage: response.data.data.pagination.currentPage,
          totalPages: response.data.data.pagination.totalPages,
          totalRides: response.data.data.pagination.totalRides,
          hasNext: response.data.data.pagination.hasNext,
          hasPrev: response.data.data.pagination.hasPrev,
          limit: response.data.data.pagination.limit,
        });
      }
    } catch (error) {
      console.error("Error fetching rides:", error);
      toast.error("Failed to fetch rides");
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_STATISTICS}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Fetch recent activity
  const fetchRecentActivity = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_RECENT_ACTIVITY}?limit=5`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setRecentActivity(response.data.data.recentRides || []);
      }
    } catch (error) {
      console.error("Error fetching recent activity:", error);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (rideId, statusData) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axiosInstance.put(
        `${API.ADMIN.RIDE_UPDATE_STATUS}/${rideId}/status`,
        statusData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Ride status updated successfully!");
      setStatusModal({ show: false, ride: null });
      fetchRides(pagination.currentPage);
      fetchStatistics();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleMobileNumberSharedChange = async (ride, newValue) => {
    if (newValue === "yes") {
      setMobileShareModal({
        show: true,
        ride,
        revertToNo: !ride.mobileNumberShared,
      });
      return;
    }

    try {
      const response = await axiosInstance.put(
        `${API.ADMIN.RIDE_MOBILE_NUMBER_SHARED}/${ride._id}/mobile-number-shared`,
        { mobileNumberShared: false }
      );

      if (response.data?.success) {
        toast.success("Mobile number shared updated");
        const updatedRide = response.data.data.ride;
        setRides((prev) =>
          prev.map((r) => (r._id === updatedRide._id ? updatedRide : r))
        );
      } else {
        toast.error(
          response.data?.message || "Failed to update mobile number shared"
        );
      }
    } catch (error) {
      console.error("Error updating mobile number shared:", error);
      toast.error(
        error.response?.data?.message || "Failed to update mobile number shared"
      );
    }
  };

  const handleMobileShareModalClose = () => {
    setMobileShareModal({ show: false, ride: null, revertToNo: false });
  };

  const handleMobileShareSaved = (updatedRide) => {
    if (!updatedRide?._id) return;
    setRides((prev) =>
      prev.map((r) => (r._id === updatedRide._id ? updatedRide : r))
    );
    setMobileShareModal({ show: false, ride: null, revertToNo: false });
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleDateFilterChange = ({ startDate, endDate }) => {
    setFilters((prev) => ({
      ...prev,
      startDate: startDate ?? prev.startDate,
      endDate: endDate ?? prev.endDate,
      page: 1,
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchRides(page);
  };

  // Export rides
  const handleExport = async (format = "csv") => {
    try {
      const token = localStorage.getItem("adminToken");

      // Build query parameters for export
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (
          value &&
          typeof value === "string" &&
          value.trim() &&
          key !== "page" &&
          key !== "limit"
        ) {
          queryParams.append(key, value);
        }
      });
      queryParams.append("format", format);

      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_EXPORT}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: format === "csv" ? "blob" : "json",
        }
      );

      if (format === "csv") {
        // Handle CSV download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `rides-export-${new Date().toISOString().split("T")[0]}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("CSV export downloaded successfully!");
      } else {
        // Handle JSON download
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri =
          "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
        const link = document.createElement("a");
        link.setAttribute("href", dataUri);
        link.setAttribute(
          "download",
          `rides-export-${new Date().toISOString().split("T")[0]}.json`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("JSON export downloaded successfully!");
      }
    } catch (error) {
      console.error("Error exporting rides:", error);
      toast.error("Failed to export rides");
    }
  };

  // Open Assign Rides modal and load employees
  const openAssignRidesModal = () => {
    setShowAssignRidesModal(true);
    setAssignRidesEmployeeId("");
    setAssignRidesAssignedDate(new Date().toISOString().slice(0, 10));
    setNewEmployeesList([]);
    setAssignRidesLoading(true);
    axiosInstance
      .get(`${API.ADMIN.EMPLOYEES_LIST}?employeeType=new&limit=100`)
      .then((res) => {
        if (Array.isArray(res.data?.employees)) setNewEmployeesList(res.data.employees);
        else if (Array.isArray(res.data?.data)) setNewEmployeesList(res.data.data);
      })
      .catch(() => {
        toast.error("Failed to load new employees");
      })
      .finally(() => setAssignRidesLoading(false));
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case "Requested":
        return {
          color: "text-blue-600 bg-blue-100",
          icon: <FaClock />,
        };
      case "Accepted":
        return {
          color: "text-yellow-600 bg-yellow-100",
          icon: <FaCheckCircle />,
        };
      case "Arrived":
        return {
          color: "text-cyan-600 bg-cyan-100",
          icon: <FaMapMarkerAlt />,
        };
      case "Started":
        return {
          color: "text-green-600 bg-green-100",
          icon: <FaSpinner className="animate-spin" />,
        };
      case "Completed":
        return {
          color: "text-green-600 bg-green-100",
          icon: <FaCheckCircle />,
        };
      case "Cancelled":
        return {
          color: "text-red-600 bg-red-100",
          icon: <FaTimesCircle />,
        };
      case "Planned":
        return {
          color: "text-purple-600 bg-purple-100",
          icon: <FaCalendar />,
        };
      default:
        return {
          color: "text-gray-600 bg-gray-100",
          icon: <FaExclamationTriangle />,
        };
    }
  };

  // Build notes string from adminActions only (no reasons); truncate to word count
  const getRideNotesDisplay = (ride, maxWords = 10) => {
    let fullText = "";
    if (Array.isArray(ride.adminActions) && ride.adminActions.length > 0) {
      const notesFromActions = ride.adminActions
        .map((a) => (a && a.notes ? a.notes.trim() : ""))
        .filter(Boolean);
      if (notesFromActions.length > 0) {
        fullText = notesFromActions.join(" ").trim();
      }
    }
    if (!fullText) return { display: "N/A", full: "" };
    const words = fullText.split(/\s+/);
    if (words.length <= maxWords) return { display: fullText, full: fullText };
    const truncated = words.slice(0, maxWords).join(" ") + "...";
    return { display: truncated, full: fullText };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const displayRideId = (ride) => {
    if (ride?.rideRequestId) return String(ride.rideRequestId);
    if (ride?._id) return String(ride._id);
    return "N/A";
  };

  const renderRouteColumn = (ride, variant) => {
    const estimated = variant === "estimated";
    const { pickupAt, dropoffAt } = estimated
      ? getEstimatedRouteTimes(ride)
      : getFinalRouteTimes(ride);
    const pickupAddr = estimated ? ride.pickup?.address : ride.driverPickupLocation?.address;
    const dropoffAddr = estimated ? ride.dropoff?.address : ride.driverDropoffLocation?.address;

    return (
      <div className="max-w-xs">
        <div className="flex items-start mb-2">
          <FaMapMarkerAlt className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-600 fullText" title={pickupAddr || undefined}>
              {pickupAddr || "N/A"}
            </p>
            {pickupAt && (
              <p className="text-xs text-gray-500 mt-0.5 fullText" title={formatDate(pickupAt)}>
                {formatDate(pickupAt)}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-start">
          <FaMapMarkerAlt className="text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-600 fullText" title={dropoffAddr || undefined}>
              {dropoffAddr || "N/A"}
            </p>
            {dropoffAt && (
              <p className="text-xs text-gray-500 mt-0.5 fullText" title={formatDate(dropoffAt)}>
                {formatDate(dropoffAt)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleSortSet = (key, direction) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: key,
      sortOrder: direction,
      page: 1,
    }));
  };

  const handleSortLabelClick = (key) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: key,
      sortOrder: prev.sortBy === key && prev.sortOrder === "asc" ? "desc" : "asc",
      page: 1,
    }));
  };

  const rideSortTh = (columnKey, label, className) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={filters.sortBy}
      activeDirection={filters.sortOrder}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={className}
      theme="indigo"
    />
  );

  useEffect(() => {
    fetchRides();
    fetchStatistics();
    fetchRecentActivity();
  }, []);

  // Load new employees once for the Assigned Employee filter dropdown.
  useEffect(() => {
    axiosInstance
      .get(`${API.ADMIN.EMPLOYEES_LIST}?employeeType=new&limit=100`)
      .then((res) => {
        const list = res.data?.employees || res.data?.data || [];
        setAssignedEmployeeFilterOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (stateUtDropdownRef.current && !stateUtDropdownRef.current.contains(e.target)) {
        setStateUtDropdownOpen(false);
      }
    };
    if (stateUtDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [stateUtDropdownOpen]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRides(filters.page);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  if (isNewType && !ridesModule) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">You do not have access to the Rides module.</p>
        </div>
      </div>
    );
  }

  const allCurrentSelected =
    rides.length > 0 &&
    rides.every((r) => selectedRideIds.includes(r._id));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Ride Management
            </h1>
            <p className="text-gray-600">
              Monitor and manage all ride bookings and their lifecycle
            </p>
          </div>
          {/* <div className="flex space-x-2">
            <button
              onClick={() => handleExport("csv")}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <FaDownload className="mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport("json")}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FaDownload className="mr-2" />
              Export JSON
            </button>
          </div> */}
        </div>
      </div>

      {/* Statistics Cards */}
      {/* {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaCar className="text-blue-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Total Rides</p>
                <p className="text-2xl font-bold text-blue-600">
                  {statistics.overall?.totalRides || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaCheckCircle className="text-green-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.overall?.completedRides || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaDollarSign className="text-purple-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{statistics.overall?.totalRevenue?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaTimesCircle className="text-red-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Completion Rate</p>
                <p className="text-2xl font-bold text-red-600">
                  {statistics.overall?.completionRate || 0}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search rides..."
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </div>

          {/* Customer name or phone */}
          <div className="relative">
            <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Customer name or phone..."
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
              value={filters.customerSearch}
              onChange={(e) => handleFilterChange("customerSearch", e.target.value)}
            />
          </div>

          {/* Driver name or phone */}
          <div className="relative">
            <FaCar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Driver name or phone..."
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
              value={filters.driverSearch}
              onChange={(e) => handleFilterChange("driverSearch", e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="border rounded-lg px-3 py-2"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Requested">Requested</option>
            <option value="Accepted">Accepted</option>
            <option value="Started">Started</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Planned">Planned</option>
            <option value="Arrived">Arrived</option>
            <option value="active">Active (All)</option>
          </select>

          <RideReportDateFilters
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateFilterChange}
          />

          {/* Trip Type Filter */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trip Type
            </label>
            <select
              className="border rounded-lg px-3 py-2"
              value={filters.tripType}
              onChange={(e) => handleFilterChange("tripType", e.target.value)}
            >
              <option value="">All Trip Types</option>
              <option value="One-Way">One-Way</option>
              <option value="Round-Trip">Round-Trip</option>
            </select>
          </div>
          

          {/* Payment Status Filter */}
          {/* <select
            className="border rounded-lg px-3 py-2"
            value={filters.paymentStatus}
            onChange={(e) =>
              handleFilterChange("paymentStatus", e.target.value)
            }
          >
            <option value="">All Payment Status</option>
            <option value="Pending">Pending</option>
            <option value="Paid">Paid</option>
            <option value="Failed">Failed</option>
          </select> */}

          {/* Payment Method Filter */}
          {/* <select
            className="border rounded-lg px-3 py-2"
            value={filters.paymentMethod}
            onChange={(e) =>
              handleFilterChange("paymentMethod", e.target.value)
            }
          >
            <option value="">All Payment Methods</option>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
          </select> */}


          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              placeholder="Search by city..."
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ride ID
            </label>
            <input
              type="text"
              placeholder="Search by ride ID..."
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.rideId}
              onChange={(e) => handleFilterChange("rideId", e.target.value)}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Pickup Location
            </label>
            <input
              type="text"
              placeholder="Search estimated pickup..."
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.estimatedPickupLocation}
              onChange={(e) =>
                handleFilterChange("estimatedPickupLocation", e.target.value)
              }
            />
          </div>
          <div className="relative min-w-0" ref={stateUtDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              States & UT
            </label>
            <button
              type="button"
              onClick={() => {
                setStateUtDropdownOpen((o) => !o);
                if (!stateUtDropdownOpen) setStateUtSearch("");
              }}
              className="w-full px-3 py-2 border rounded-lg bg-white text-sm text-left flex items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <span className={filters.stateUt ? "text-gray-900" : "text-gray-500"}>
                {getStateUtDisplayLabel(filters.stateUt)}
              </span>
              <FaSearch className="text-gray-400 ml-2 shrink-0" />
            </button>
            {stateUtDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={stateUtSearch}
                    onChange={(e) => setStateUtSearch(e.target.value)}
                    placeholder="Search state or UT..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                </div>
                <ul className="max-h-48 overflow-y-auto py-1">
                  {STATE_UT_OPTIONS.filter((opt) => {
                    if (opt.type === "header") {
                      return !stateUtSearch.trim();
                    }
                    return (
                      !stateUtSearch.trim() ||
                      opt.label.toLowerCase().includes(stateUtSearch.trim().toLowerCase())
                    );
                  }).map((opt) =>
                    opt.type === "header" ? (
                      <li key="rides-state-ut-header">
                        <div className="px-4 py-2 text-xs font-semibold bg-yellow-50 text-gray-700 border-y border-yellow-100">
                          {opt.label}
                        </div>
                      </li>
                    ) : (
                      <li key={opt.value || "__rides_state_empty__"}>
                        <button
                          type="button"
                          onClick={() => {
                            handleFilterChange("stateUt", opt.value);
                            setStateUtDropdownOpen(false);
                            setStateUtSearch("");
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-blue-50 ${filters.stateUt === opt.value ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-800"
                            }`}
                        >
                          {opt.label}
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.sortBy}
              onChange={(e) => handleFilterChange("sortBy", e.target.value)}
            >
              <option value="createdAt">Created Date</option>
              <option value="fare">Fare</option>
              <option value="distance">Distance</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fare
            </label>
            <input
              type="text"
              placeholder="Search by fare..."
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.fare}
              onChange={(e) => handleFilterChange("fare", e.target.value)}
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Employee
            </label>
            <select
              className="border rounded-lg px-3 py-2 w-full"
              value={filters.assignedEmployeeId}
              onChange={(e) =>
                handleFilterChange("assignedEmployeeId", e.target.value)
              }
            >
              <option value="">All Employees</option>
              {assignedEmployeeFilterOptions.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.fullName || emp.name || "Unnamed"}
                  {emp.role ? ` (${emp.role})` : ""}
                  {emp.city ? ` - ${emp.city}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Status breakdown panel — counts mirror current filter set */}
      <RidesStatusStatsPanel
        filters={filters}
        onStatusClick={(status) => handleFilterChange("status", status)}
        onSummaryFilter={(nextFilters) =>
          setFilters((prev) => ({
            ...prev,
            ...nextFilters,
            page: 1,
          }))
        }
      />

      {/* Assigned-employee stats — visible only when an employee is selected */}
      <RidesAssignedEmployeeStatsPanel
        filters={filters}
        onCardFilter={(nextFilters) =>
          setFilters((prev) => ({ ...prev, ...nextFilters, page: 1 }))
        }
      />

      {/* Rides List */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex justify-end gap-2 relative px-4 pt-4">
          {canAssignRides && (
            <button
              type="button"
              disabled={selectedRideIds.length === 0}
              onClick={openAssignRidesModal}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign the rides
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowColumnOptions((v) => !v)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
          >
            <FaColumns className="mr-2" />
            Columns
          </button>
          {showColumnOptions && (
            <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-white border rounded-lg shadow-lg z-30 min-w-[220px]">
              {RIDE_TABLE_COLUMN_OPTIONS.map(([col, label]) => (
                <label
                  key={col}
                  className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns[col]}
                    onChange={(e) =>
                      setVisibleColumns((v) => ({
                        ...v,
                        [col]: e.target.checked,
                      }))
                    }
                    className="rounded"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading rides...</span>
          </div>
        ) : rides.length === 0 ? (
          <div className="text-center py-12">
            <FaCar className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500">
              No rides found matching your criteria
            </p>
          </div>
        ) : (
          <div
            ref={tableScrollContainerRef}
            className="overflow-x-auto overflow-y-auto max-h-[70vh]"
          >
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
                <tr>
                  {canAssignRides && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={allCurrentSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRideIds(rides.map((r) => r._id));
                          } else {
                            setSelectedRideIds([]);
                          }
                        }}
                      />
                    </th>
                  )}
                  {visibleColumns.rideId &&
                    rideSortTh(
                      "rideId",
                      "Ride ID",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[16rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  {visibleColumns.rideCreated &&
                    rideSortTh(
                      "createdAt",
                      "Ride Created",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[12rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  {visibleColumns.rideScheduled &&
                    rideSortTh(
                      "scheduledAt",
                      "Ride Scheduled",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 min-w-[12rem]"
                    )}
                  {visibleColumns.estimatedRoute &&
                    rideSortTh(
                      "estimatedRoute",
                      "Estimated Route",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[25rem] whitespace-normal hover:bg-gray-100"
                    )}
                  {visibleColumns.finalRoute &&
                    rideSortTh(
                      "finalRoute",
                      "Final Route",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[25rem] whitespace-normal hover:bg-gray-100"
                    )}
                  {visibleColumns.started &&
                    rideSortTh(
                      "startedAt",
                      "Started",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[12rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  {visibleColumns.completed &&
                    rideSortTh(
                      "completedAt",
                      "Completed",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[12rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  {visibleColumns.rideDetails &&
                    rideSortTh(
                      "rideDetails",
                      "Ride Details",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100"
                    )}
                  {visibleColumns.commission &&
                    rideSortTh(
                      "commission",
                      "Commission (10%)",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 min-w-[7rem]"
                    )}
                  {visibleColumns.tax &&
                    rideSortTh(
                      "tax",
                      "Tax",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 min-w-[6rem]"
                    )}
                  {visibleColumns.dueStatus &&
                    rideSortTh(
                      "dueStatus",
                      "Due Status",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap hover:bg-gray-100 min-w-[7rem]"
                    )}
                  {visibleColumns.customer &&
                    rideSortTh(
                      "customer",
                      "Customer",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 min-w-[14rem]"
                    )}
                  {visibleColumns.driver &&
                    rideSortTh(
                      "driver",
                      "Driver",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 min-w-[14rem]"
                    )}
                  {visibleColumns.assignedEmployee &&
                    rideSortTh(
                      "assignedEmployee",
                      "Assigned Employee",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-40 hover:bg-gray-100"
                    )}
                  {visibleColumns.assignedBy &&
                    rideSortTh(
                      "assignedBy",
                      "Assigned By",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-40 hover:bg-gray-100"
                    )}
                  {visibleColumns.rideStatus &&
                    rideSortTh(
                      "status",
                      "Ride Status",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100"
                    )}
                  {visibleColumns.reason &&
                    rideSortTh(
                      "reason",
                      "Reason",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100"
                    )}
                  {visibleColumns.mobileNumberShared && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[10rem] whitespace-nowrap">
                      Mobile Number Shared
                    </th>
                  )}
                  {visibleColumns.mobileNumberSharedParty && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[8rem] whitespace-nowrap">
                      Shared With
                    </th>
                  )}
                  {visibleColumns.mobileNumberSharedCallerId &&
                    rideSortTh(
                      "mobileNumberSharedCallerId",
                      "Caller ID",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[10rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  {visibleColumns.mobileNumberSharedTimes &&
                    rideSortTh(
                      "mobileNumberSharedTimes",
                      "Shared Times",
                      "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[10rem] whitespace-nowrap hover:bg-gray-100"
                    )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rides.map((ride) => (
                  <tr
                    key={ride._id}
                    className={`hover:bg-gray-50 ${ride.assignedTo ? "bg-yellow-50" : ""}`}
                  >
                    {canAssignRides && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRideIds.includes(ride._id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedRideIds((prev) =>
                              checked
                                ? [...new Set([...prev, ride._id])]
                                : prev.filter((id) => id !== ride._id)
                            );
                          }}
                        />
                      </td>
                    )}
                    {visibleColumns.rideId && (
                    <td className="px-6 py-4 min-w-[16rem]">
                      <p className="text-sm font-mono text-gray-900 whitespace-nowrap" title={displayRideId(ride)}>
                        {displayRideId(ride)}
                      </p>
                    </td>
                    )}
                    {visibleColumns.rideCreated && (
                      <td className="px-6 py-4 min-w-[12rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">{formatDate(ride.createdAt)}</p>
                      </td>
                    )}
                    {visibleColumns.rideScheduled && (
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700">
                          {ride.scheduledAt ? formatDate(ride.scheduledAt) : "N/A"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.estimatedRoute && (
                      <td className="px-6 py-4">{renderRouteColumn(ride, "estimated")}</td>
                    )}
                    {visibleColumns.finalRoute && (
                      <td className="px-6 py-4">{renderRouteColumn(ride, "final")}</td>
                    )}
                    {visibleColumns.started && (
                      <td className="px-6 py-4 min-w-[12rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {ride.startedAt ? formatDate(ride.startedAt) : "N/A"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.completed && (
                      <td className="px-6 py-4 min-w-[12rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {ride.completedAt ? formatDate(ride.completedAt) : "N/A"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.rideDetails && (
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center mb-1">
                            <FaCar className="text-blue-500 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {ride.carType} • {ride.transmission}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">
                            {ride.tripType}
                          </p>
                          <div className="flex items-center mt-1">
                            <span className="text-sm font-medium text-green-600">
                              ₹{ride.fare}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              {ride.distance}km
                            </span>
                            {ride.actualEstimatedTime !== null && ride.actualEstimatedTime !== undefined && (
                              <span className="text-xs text-gray-500 ml-2 flex items-center">
                                <FaClock className="mr-1" />
                                {formatDuration(ride.actualEstimatedTime)}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    )}

                    {visibleColumns.commission && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{formatCommissionColumn(ride)}</p>
                      </td>
                    )}
                    {visibleColumns.tax && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-900">{formatTaxColumn(ride)}</p>
                      </td>
                    )}
                    {visibleColumns.dueStatus && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DueStatusCell ride={ride} />
                      </td>
                    )}

                    {visibleColumns.customer && (
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-start">
                          <FaUser className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 max-w-[16rem]">
                            <p className="text-sm font-medium text-gray-900">
                              {ride.userId?.fullName || "N/A"}
                            </p>
                            <div className="flex items-center text-sm text-gray-500">
                              <FaPhone className="mr-1 flex-shrink-0" />
                              {ride.userId?.phone || "N/A"}
                            </div>
                            <p className="text-xs text-gray-400">
                              {ride.userId?.city || "N/A"}
                            </p>
                            {(() => {
                              const ratingVal = getRideDriverRating(ride);
                              const reviewText = getRideDriverReview(ride);
                              const showRating = Number.isFinite(ratingVal);
                              if (!showRating && !reviewText) return null;
                              return (
                                <div className="mt-2 pt-2 border-t border-gray-100 text-xs space-y-1.5">
                                  {showRating && (
                                    <div className="text-gray-800">
                                      <span className="font-medium text-gray-600">Rating: </span>
                                      <span>{formatRideRatingCell(ratingVal)}</span>
                                      <span className="text-gray-500"> (driver)</span>
                                    </div>
                                  )}
                                  {reviewText ? (
                                    <ExpandableReviewText text={reviewText} />
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                    )}

                    {visibleColumns.driver && (
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-start">
                          <FaUser className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0 max-w-[16rem]">
                            {ride.driverId ? (
                              <>
                                <p className="text-sm font-medium text-gray-900">
                                  {ride.driverId.fullName}
                                </p>
                                <div className="flex items-center text-sm text-gray-500">
                                  <FaPhone className="mr-1 flex-shrink-0" />
                                  {ride.driverId.phone}
                                </div>
                                <p className="text-xs text-gray-400">
                                  {ride.driverId.vehicle?.make}{" "}
                                  {ride.driverId.vehicle?.model}
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">Not assigned</p>
                            )}
                            {(() => {
                              const ratingVal = getRideCustomerRating(ride);
                              const reviewText = getRideCustomerReview(ride);
                              const showRating = Number.isFinite(ratingVal);
                              if (!showRating && !reviewText) return null;
                              return (
                                <div className="mt-2 pt-2 border-t border-gray-100 text-xs space-y-1.5">
                                  {showRating && (
                                    <div className="text-gray-800">
                                      <span className="font-medium text-gray-600">Rating: </span>
                                      <span>{formatRideRatingCell(ratingVal)}</span>
                                      <span className="text-gray-500"> (customer)</span>
                                    </div>
                                  )}
                                  {reviewText ? (
                                    <ExpandableReviewText text={reviewText} />
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                    )}

                    {visibleColumns.assignedEmployee && (
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {ride.assignedTo?.fullName || "N/A"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {ride.assignedDate
                              ? new Date(ride.assignedDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                              : "N/A"}
                          </p>
                        </div>
                      </td>
                    )}

                    {visibleColumns.assignedBy && (
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {ride.assignedBy?.fullName || "N/A"}
                        </p>
                      </td>
                    )}

                    {visibleColumns.rideStatus && (
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center mb-2">
                            {(() => {
                              const statusDisplay = getStatusDisplay(ride.status);
                              return (
                                <>
                                  {statusDisplay.icon}
                                  <span
                                    className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.color}`}
                                  >
                                    {ride.status}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                          <div className="text-xs text-gray-500 space-y-1">
                            {(() => {
                              const { display, full } = getRideNotesDisplay(ride);
                              const notesDisplay = display && display !== "N/A" ? display : "N/A";
                              const notesBlock = !full ? (
                                <span>Notes: {notesDisplay}</span>
                              ) : (
                                <span className="group relative inline-block">
                                  Notes: {notesDisplay}
                                  <span
                                    className="invisible group-hover:visible absolute left-0 bottom-full z-10 mb-1 w-64 max-w-[90vw] rounded bg-gray-800 px-2 py-2 text-xs text-white shadow-lg whitespace-pre-wrap break-words"
                                    role="tooltip"
                                  >
                                    {full}
                                  </span>
                                </span>
                              );
                              return <div>{notesBlock}</div>;
                            })()}
                          </div>
                        </div>
                      </td>
                    )}

                    {visibleColumns.reason && (
                      <td className="px-6 py-4">
                        <p className="text-xs text-red-600 max-w-xs break-words">
                          {(ride.cancellationReason && ride.cancellationReason.trim()) || "N/A"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.mobileNumberShared && (
                      <td className="px-6 py-4 min-w-[10rem]">
                        <select
                          value={ride.mobileNumberShared ? "yes" : "no"}
                          onChange={(e) =>
                            handleMobileNumberSharedChange(ride, e.target.value)
                          }
                          disabled={!canEditRides}
                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                        {canEditRides && ride.mobileNumberShared && (
                          <button
                            type="button"
                            onClick={() =>
                              setMobileShareModal({
                                show: true,
                                ride,
                                revertToNo: false,
                              })
                            }
                            className="text-xs text-blue-600 hover:text-blue-800 mt-1 block"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                    {visibleColumns.mobileNumberSharedParty && (
                      <td className="px-6 py-4 min-w-[8rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {ride.mobileNumberShared
                            ? ride.mobileNumberSharedParty || "-"
                            : "-"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.mobileNumberSharedCallerId && (
                      <td className="px-6 py-4 min-w-[10rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {ride.mobileNumberShared
                            ? ride.mobileNumberSharedCallerId || "-"
                            : "-"}
                        </p>
                      </td>
                    )}
                    {visibleColumns.mobileNumberSharedTimes && (
                      <td className="px-6 py-4 min-w-[10rem]">
                        <p className="text-sm text-gray-700 whitespace-nowrap">
                          {ride.mobileNumberShared
                            ? ride.mobileNumberSharedTimes || "-"
                            : "-"}
                        </p>
                      </td>
                    )}

                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        {canViewRides && (
                          <button
                            onClick={() => setDetailModal({ show: true, ride })}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                        )}
                        {canEditRides && (
                          <button
                            onClick={() => setStatusModal({ show: true, ride })}
                            className="text-orange-600 hover:text-orange-800"
                            title="Update Status"
                          >
                            <FaEdit />
                          </button>
                        )}
                        {ride.hasActivityLogs && (
                          <button
                            onClick={() =>
                              setActivityLogsModal({
                                show: true,
                                module: "Ride",
                                entityId: ride._id,
                              })
                            }
                            className="text-indigo-600 hover:text-indigo-800 inline-flex items-center text-sm p-2 rounded hover:bg-indigo-50"
                            title="View Activity Logs"
                          >
                            <FaHistory />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalRides
              )}{" "}
              of {pagination.totalRides} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {Array.from(
                { length: Math.min(pagination.totalPages, 5) },
                (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${pageNum === pagination.currentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : "hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
              )}

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAssignRidesModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => !assignRidesSubmitLoading && setShowAssignRidesModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Assign rides to employee
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {selectedRideIds.length} ride(s) selected. Choose a new employee to assign.
            </p>
            {assignRidesLoading ? (
              <div className="flex items-center gap-2 text-gray-500 py-4">
                <FaSpinner className="animate-spin" /> Loading employees...
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New employee
                </label>
                <select
                  value={assignRidesEmployeeId}
                  onChange={(e) => setAssignRidesEmployeeId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">— Select employee —</option>
                  {newEmployeesList.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.fullName || emp.name || "Unnamed"}
                      {emp.role ? ` (${emp.role})` : ""}
                      {emp.city ? ` – ${emp.city}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned date
              </label>
              <input
                type="date"
                value={assignRidesAssignedDate}
                onChange={(e) => setAssignRidesAssignedDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={assignRidesSubmitLoading}
                onClick={() => !assignRidesSubmitLoading && setShowAssignRidesModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  assignRidesSubmitLoading || assignRidesLoading || !assignRidesEmployeeId
                }
                onClick={async () => {
                  if (!assignRidesEmployeeId || selectedRideIds.length === 0) return;
                  setAssignRidesSubmitLoading(true);
                  try {
                    const res = await axiosInstance.post(API.ADMIN.RIDES_BULK_ASSIGN, {
                      rideIds: selectedRideIds,
                      employeeId: assignRidesEmployeeId,
                      assignedDate: assignRidesAssignedDate,
                    });
                    if (res.data?.success) {
                      toast.success(res.data.message || "Rides assigned successfully");
                      setShowAssignRidesModal(false);
                      setSelectedRideIds([]);
                      setAssignRidesEmployeeId("");
                      setAssignRidesAssignedDate(new Date().toISOString().slice(0, 10));
                      fetchRides(pagination.currentPage);
                    } else {
                      toast.error(res.data?.message || "Failed to assign rides");
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to assign rides");
                  } finally {
                    setAssignRidesSubmitLoading(false);
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {assignRidesSubmitLoading ? (
                  <FaSpinner className="animate-spin" />
                ) : null}
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <RideDetailModal
        show={detailModal.show}
        ride={detailModal.ride}
        onClose={() => setDetailModal({ show: false, ride: null })}
        onDriverAssigned={() => {
          fetchRides(pagination.currentPage);
        }}
        onRideUpdated={(updatedRide) => {
          if (!updatedRide?._id) return;
          setRides((prev) =>
            prev.map((r) => (r._id === updatedRide._id ? updatedRide : r))
          );
          setDetailModal((prev) =>
            prev.show && prev.ride?._id === updatedRide._id
              ? { ...prev, ride: updatedRide }
              : prev
          );
        }}
      />

      <RideStatusUpdateModal
        show={statusModal.show}
        ride={statusModal.ride}
        onClose={() => setStatusModal({ show: false, ride: null })}
        onUpdate={handleStatusUpdate}
      />
      <MobileNumberSharedModal
        show={mobileShareModal.show}
        ride={mobileShareModal.ride}
        onClose={handleMobileShareModalClose}
        onSaved={handleMobileShareSaved}
      />
      <ActivityLogsModal
        show={activityLogsModal.show}
        module={activityLogsModal.module}
        entityId={activityLogsModal.entityId}
        onClose={() =>
          setActivityLogsModal({ show: false, module: "Ride", entityId: null })
        }
      />
    </div>
  );
};

export default Rides;
